import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CalendarDays } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface PersonalityTest {
  id: string;
  user_id: string;
  personality_type: string;
  created_at: string;
  details?: Record<string, any>;
}

interface SavedCareer {
  id: string;
  user_id: string;
  career_id: string;
  career_title: string;
  created_at: string;
}

const ResultsPage = () => {
  const { user } = useAuth();
  const [personalityTests, setPersonalityTests] = useState<PersonalityTest[]>([]);
  const [savedCareers, setSavedCareers] = useState<SavedCareer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Fetch personality tests
        const { data: testsData, error: testsError } = await supabase
          .from('personality_tests')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (testsError) throw testsError;
        setPersonalityTests(testsData || []);
        
        // Fetch saved careers
        const { data: careersData, error: careersError } = await supabase
          .from('saved_careers')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (careersError) throw careersError;
        setSavedCareers(careersData || []);
        
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load your results.",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [user]);
  
  const handleRemoveSavedCareer = async (careerId: string) => {
    try {
      const { error } = await supabase
        .from('saved_careers')
        .delete()
        .eq('id', careerId);
        
      if (error) throw error;
      
      // Update state to remove the deleted career
      setSavedCareers(savedCareers.filter(career => career.id !== careerId));
      
      toast({
        title: "Career Removed",
        description: "The career has been removed from your saved list.",
      });
    } catch (error) {
      console.error('Error removing saved career:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove the saved career.",
      });
    }
  };
  
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container mx-auto px-4 py-24">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Navbar />
      <div className="container mx-auto px-4 py-24">
        <Card>
          <CardHeader>
            <CardTitle>My Results</CardTitle>
            <CardDescription>
              View your personality test results and saved career matches.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="personality-tests" className="space-y-4">
              <TabsList>
                <TabsTrigger value="personality-tests">Personality Tests</TabsTrigger>
                <TabsTrigger value="saved-careers">Saved Careers</TabsTrigger>
              </TabsList>
              
              <TabsContent value="personality-tests" className="space-y-4">
                {personalityTests.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">You haven't taken any personality tests yet.</p>
                    <Button className="mt-4" asChild>
                      <a href="/">Take a Test</a>
                    </Button>
                  </div>
                ) : (
                  personalityTests.map((test) => (
                    <Card key={test.id} className="overflow-hidden">
                      <CardHeader className="bg-muted/50 pb-2">
                        <div className="flex justify-between items-center">
                          <Badge variant="outline" className="font-semibold">
                            {test.personality_type}
                          </Badge>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {new Date(test.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <p>
                          You are a <strong>{test.personality_type}</strong> personality type.
                        </p>
                        {/* We could add more details about the personality type here */}
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
              
              <TabsContent value="saved-careers" className="space-y-4">
                {savedCareers.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">You haven't saved any careers yet.</p>
                    <Button className="mt-4" asChild>
                      <a href="/">Explore Careers</a>
                    </Button>
                  </div>
                ) : (
                  savedCareers.map((career) => (
                    <Card key={career.id} className="overflow-hidden">
                      <CardHeader className="bg-muted/50 pb-2">
                        <div className="flex justify-between items-center">
                          <h3 className="font-medium">{career.career_title}</h3>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <CalendarDays className="h-3.5 w-3.5" />
                            {new Date(career.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-center">
                          <Button variant="outline" asChild>
                            <a href={`/careers/${career.career_id}`}>View Details</a>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemoveSavedCareer(career.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </>
  );
};

export default ResultsPage;
